import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  navigateToConfigurationSession,
  renameSessionAndNavigate,
} from 'src/features/forecastPaginated/forecastPaginatedActions/hooks/forecastPaginatedLastActionUtils';
import type { SessionSummary } from 'src/services/api/getForecastSessions';
import { useRenameForecastSession } from 'src/services/api/renameForecastSession';
import { useBoundStore } from 'src/store/store';

export function useRenameSessionAfterLastAction() {
  const { t } = useTranslation('forecast');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setForecastSessionRedirectSuppressed = useBoundStore(
    (state) => state.setForecastSessionRedirectSuppressed,
  );
  const [sessionToRename, setSessionToRename] = useState<SessionSummary | null>(
    null,
  );
  const [nextSessionName, setNextSessionName] = useState('');
  const { mutateAsync: renameForecastSession, isPending: isRenamingSession } =
    useRenameForecastSession();

  useEffect(() => {
    return () => {
      setForecastSessionRedirectSuppressed(false);
    };
  }, [setForecastSessionRedirectSuppressed]);

  const closeRenameSessionModal = () => {
    setSessionToRename(null);
    setNextSessionName('');
  };

  const renameSessionAndNavigateToConfiguration = async (
    session: SessionSummary,
    sessionName: string,
  ) => {
    await renameSessionAndNavigate({
      navigate,
      nextSessionName: sessionName,
      queryClient,
      renameForecastSession,
      sessionToRename: session,
    });
    setForecastSessionRedirectSuppressed(false);
  };

  const handleConfirmRename = async () => {
    if (!(sessionToRename && nextSessionName.trim())) {
      return;
    }

    try {
      await renameSessionAndNavigateToConfiguration(
        sessionToRename,
        nextSessionName,
      );
      closeRenameSessionModal();
    } catch {
      toast.error(t('Error'), {
        description: t('Failed to rename session'),
      });
    }
  };

  const handleCancelRename = async () => {
    if (!sessionToRename) {
      return;
    }

    const fallbackSessionId = sessionToRename.id;
    closeRenameSessionModal();
    setForecastSessionRedirectSuppressed(false);
    await navigateToConfigurationSession(navigate, fallbackSessionId);
  };

  return {
    isRenamingSession,
    nextSessionName,
    renameModalOpen: Boolean(sessionToRename),
    setNextSessionName,
    renameSessionAndNavigateToConfiguration,
    openRenameSession: (
      session: SessionSummary,
      initialName = session.name,
    ) => {
      setForecastSessionRedirectSuppressed(true);
      setSessionToRename(session);
      setNextSessionName(initialName);
    },
    handleCancelRename,
    handleConfirmRename,
  };
}
